import { resolveLocationId } from '../../../helpers/resolveLocationId';
import { getVisibleSurveyQuestions } from '../../../helpers/surveyQuestionVisibility';
import {
  getGridspotConfigurationError,
  isGridspotQuestion,
} from '../../../helpers/gridspot';
import { questionTypes } from '../survey/SurveyPreviewBody';
import { getOptionId, isSameId } from '../survey/surveyPreviewUtils';

/** Question types shown in Client Connect v2 feedback UI (excluded from validation too). */
const HIDDEN_FEEDBACK_QUESTION_TYPES = new Set([
  questionTypes.EMPLOYEES,
  'director_message',
  'skip',
]);

const EMAIL_PATTERN = new RegExp(
  /^(("[\w-\s]+")|([\w-]+(?:\.[\w-]+)*)|("[\w-\s]+")([\w-]+(?:\.[\w-]+)*))(@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$)|(@\[?((25[0-5]\.|2[0-4][0-9]\.|1[0-9]{2}\.|[0-9]{1,2}\.))((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\.){2}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\]?$)/i
);

export function getClientConnectFeedbackQuestions(questions = []) {
  return questions.filter((question) => !HIDDEN_FEEDBACK_QUESTION_TYPES.has(question.questionType));
}

/**
 * Feedback questions after hide/route + inventory location gating (Normal/L&D rules).
 * Indices for hide_questions / route_to_index use the original survey.questions array.
 */
export function getClientConnectVisibleQuestions(survey, answers = {}) {
  const hasLocation = Boolean(resolveLocationId(survey));
  const visible = getVisibleSurveyQuestions(survey?.questions, answers, {
    hasLocation,
    includeEmployees: false,
  });

  return visible.map((question, index) => ({
    ...question,
    displayIndex: index + 1,
  }));
}

function isMissingGridAnswerValue(value, question) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return true;
  }

  const rows = question?.gridRows ?? [];
  if (!rows.length) {
    return Object.keys(value).length === 0;
  }

  const isMultiselect = question?.gridInputType === 'multiselect';

  return rows.some((row) => {
    const rowKey = row._id != null ? String(row._id) : String(row.order);
    const rowAnswer = value[rowKey] ?? value[row._id];

    if (isMultiselect) {
      return !rowAnswer || !Array.isArray(rowAnswer) || rowAnswer.length === 0;
    }

    return rowAnswer == null || rowAnswer === '';
  });
}

/** Empty-check for Client Connect v2 validation (grid uses dedicated row checks). */
export function isMissingSurveyAnswerValue(value, question = null) {
  if (question?.questionType === questionTypes.GRID) {
    return isMissingGridAnswerValue(value, question);
  }

  if (value == null) {
    return true;
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  if (typeof value === 'object') {
    return Object.keys(value).length === 0;
  }

  return !value.toString()?.length;
}

function areEmailsValid(value) {
  const emails = String(value)
    .split(',')
    .map((s) => s.trim());
  return emails.every((email) => email.length > 0 && EMAIL_PATTERN.test(email));
}

function getPhoneDigits(value) {
  return String(value).replace(/\D/g, '');
}

function isValidPhoneAnswer(value) {
  return getPhoneDigits(value).length === 10;
}

export function getSelectedOptions(question, answerValue) {
  if (!answerValue) {
    return [];
  }

  const answerArr = Array.isArray(answerValue) ? answerValue : [answerValue];
  return (question.options || []).filter((opt, index) => {
    const optionId = getOptionId(opt, index);
    return answerArr.some((id) => isSameId(id, optionId));
  });
}

export function choiceAnswerNeedsWriteIn(question, answerValue) {
  if (!answerValue || isGridspotQuestion(question)) {
    return false;
  }
  return getSelectedOptions(question, answerValue).some((opt) => opt?.write_in);
}

export function choiceAnswerNeedsImage(question, answerValue) {
  if (!answerValue || isGridspotQuestion(question)) {
    return false;
  }
  return getSelectedOptions(question, answerValue).some((opt) => opt?.image_required);
}

/**
 * Returns the first validation error for a feedback question, or null if valid.
 * Mirrors normal survey rules for required, min/max, text, number, write-in, and image.
 */
export function getClientConnectQuestionValidationError(question, answer, intl) {
  const { required, min_options: minOptions = null, max_options: maxOptions = null } = question;
  const value = answer?.value;

  if (isGridspotQuestion(question) && getGridspotConfigurationError(question)) {
    if (required) {
      return {
        message: intl.formatMessage({
          id: 'gridspot-question-unavailable',
          defaultMessage:
            'This question is not set up correctly. Please contact support.',
        }),
      };
    }
    return null;
  }

  if (required && isMissingSurveyAnswerValue(value, question)) {
    return { message: intl.formatMessage({ id: 'this-question-is-required' }) };
  }

  if (!isGridspotQuestion(question)) {
    if ((minOptions || maxOptions) && (!value || !Array.isArray(value))) {
      return { message: intl.formatMessage({ id: 'this-question-is-required' }) };
    }

    if (minOptions && minOptions > 0 && value?.length < minOptions) {
      return {
        message: intl.formatMessage({ id: 'select-atleast-min-options' }, { minOptions }),
      };
    }

    if (maxOptions && maxOptions > 0 && value?.length > maxOptions) {
      return {
        message: intl.formatMessage({ id: 'select-max-options' }, { maxOptions }),
      };
    }
  }

  if (question.questionType === questionTypes.TEXT && question.textType === 'textbox') {
    if (question.minimum_character && question.minimum_character > 0 && value) {
      if (String(value).trim().length < question.minimum_character) {
        return {
          message: intl.formatMessage(
            { id: 'please-enter-at-least' },
            { min: question.minimum_character },
          ),
        };
      }
    }
  }

  if (question.questionType === questionTypes.TEXT && question.textType === 'email') {
    if (value && String(value).trim().length > 0 && !areEmailsValid(value)) {
      return { message: intl.formatMessage({ id: 'please-enter-valid-email-address' }) };
    }
  }

  if (question.questionType === questionTypes.TEXT && question.textType === 'phone') {
    if (value && String(value).trim().length > 0 && !isValidPhoneAnswer(value)) {
      return { message: intl.formatMessage({ id: 'phone-number-should-be-10-digit' }) };
    }
  }

  if (question.questionType === questionTypes.NUMBER) {
    if (question.max_number_value && Number(question.max_number_value) > 0 && value) {
      if (
        String(value).trim().length > 0 &&
        Number(value) > Number(question.max_number_value)
      ) {
        return {
          message: intl.formatMessage(
            { id: 'please-enter-a-number-less-than-or-equal-to' },
            { max_number_value: question.max_number_value },
          ),
        };
      }
    }
  }

  if (value && !isGridspotQuestion(question)) {
    const selectedOptions = getSelectedOptions(question, value);
    if (selectedOptions.some((opt) => opt?.write_in)) {
      const writeIn = answer?.write_in?.trim();
      if (!writeIn || writeIn.length === 0) {
        return { message: intl.formatMessage({ id: 'comment-is-required' }) };
      }
    }

    if (selectedOptions.some((opt) => opt?.image_required)) {
      const imageCount = answer?.image?.length;
      if (!imageCount || imageCount === 0) {
        return { message: intl.formatMessage({ id: 'image-is-required' }) };
      }
    }
  }

  return null;
}
